import React, { useState, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { Drawer } from 'vaul';
import type { DialogProps, DialogStore } from '@ariakit/react';
import { Dialog, DialogProvider, useStoreState } from '@ariakit/react';
import { useMediaQuery } from '@periodica/hooks';
import { DialogCloseButton } from '../../../DialogCloseButton';
import { ToastContentClassName } from '../../../Toast';
import { ModalContext } from '../ModalContext';

import * as styles from './index.module.css';

type DialogSize = 'small' | 'medium' | 'large' | 'xLarge' | 'full';

type DialogWrapperArgs = {
  type?: 'dialog' | 'drawer';
  store?: DialogStore;
  size?: DialogSize;
  hasCloseButton?: boolean;
  className?: string;
  open?: boolean;
  onClose?: () => void;
  overlay?: boolean;
};

export type DialogWrapperProps = DialogWrapperArgs & Omit<DialogProps, 'store'>;

interface DrawerContentWrapperArgs {
  portal?: boolean;
  children: React.ReactNode;
}

function DrawerContentWrapper({ portal, children }: DrawerContentWrapperArgs) {
  return portal ? <Drawer.Portal>{children}</Drawer.Portal> : children;
}

export function Wrapper({
  type = 'dialog',
  size = 'medium',
  overlay = true,
  hasCloseButton = true,
  open,
  onClose,
  store,
  children,
  className = '',
  ...rest
}: DialogWrapperProps) {
  const [isClient, setIsClient] = useState(false);
  const { portal } = rest;

  useEffect(() => setIsClient(true), []);
  const openDrawer = useStoreState(store, 'open') ?? open;

  const isMobile = useMediaQuery('(max-width: 767px)', true);
  const isDrawer = !!(isClient && type === 'drawer' && isMobile);

  const contextValue = useMemo(() => ({ type, isDrawer }), [type, isDrawer]);

  const handleOpenChange = (drawerShow: boolean) => {
    if (drawerShow) store?.show();
    else store?.hide();
  };

  if (isDrawer) {
    return (
      <ModalContext.Provider value={contextValue}>
        <Drawer.Root
          open={openDrawer}
          onClose={onClose}
          onOpenChange={handleOpenChange}
          repositionInputs={false}
        >
          <DrawerContentWrapper portal={portal}>
            {/* overlay должен быть внутри портала, иначе начинаются проблемы с z-index */}
            {overlay && <Drawer.Overlay className={styles.overlayDrawer} />}
            <Drawer.Content className={styles.drawerContent}>
              {children}
              {hasCloseButton && <DialogCloseButton />}
            </Drawer.Content>
          </DrawerContentWrapper>
        </Drawer.Root>
      </ModalContext.Provider>
    );
  }

  const fullDesktopModal = size === 'full';

  return (
    <DialogProvider store={store}>
      <Dialog
        {...rest}
        getPersistentElements={() => document.querySelectorAll(`.${ToastContentClassName}`)}
        backdrop={!fullDesktopModal && overlay ? <div className={styles.overlay} /> : false}
        className={cn([
          styles.modal,
          styles[size],
          className,
          !hasCloseButton && styles.withoutCloseButton,
        ])}
        open={open}
        onClose={onClose}
      >
        {children}
        {hasCloseButton && (
          <DialogCloseButton className={cn(fullDesktopModal && styles.fullModalCloseButton)} />
        )}
      </Dialog>
    </DialogProvider>
  );
}
